// LEO GET SEEN / BĄDŹ WIDOCZNY — static bilingual content (template id: get_seen).
// User answers are stored under language-independent keys in
// goal.template_data.getSeen (see GetSeenGoalContent.jsx).

export const GET_SEEN_TEMPLATE_ID = "get_seen";
export const GET_SEEN_MIN_DAYS = 7;
export const GET_SEEN_RECOMMENDED_DAYS = 30;
export const GET_SEEN_DURATIONS = [7, 14, 21, 30, 50, 60, 90];

// Base day ranges for the recommended 30-day pace.
const BASE_RANGES = [[1, 2], [3, 4], [5, 7], [8, 12], [13, 17], [18, 19], [20, 23], [24, 26], [27, 28], [29, 30]];

// Scale the 10 stages proportionally onto `days`. Stages may share a day; never merged/removed.
export const getSeenDayRanges = (days) => {
  const d = Math.max(1, parseInt(days, 10) || GET_SEEN_RECOMMENDED_DAYS);
  return BASE_RANGES.map(([s, e]) => {
    const start = Math.min(d, Math.floor(((s - 1) * d) / 30) + 1);
    const end = Math.min(d, Math.max(start, Math.round((e * d) / 30)));
    return [start, end];
  });
};

const addDays = (base, n) => {
  const dt = new Date(base);
  dt.setDate(dt.getDate() + n);
  return dt;
};

export const toIsoDate = (dt) => {
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// Date of day N (1-based) of a goal started at `startDate`.
export const getSeenDayDate = (startDate, dayN) => addDays(startDate, dayN - 1);

// Steps sent with POST /career/goals when the GET SEEN goal is created.
export const buildGetSeenSteps = (lang, days, startDate = new Date()) => {
  const L = GET_SEEN_TEMPLATE[lang === "pl" ? "pl" : "en"];
  const ranges = getSeenDayRanges(days);
  return L.steps.map((s, i) => ({
    name: `${L.stepLabel} ${i + 1} — ${s.title}`,
    due: toIsoDate(getSeenDayDate(startDate, ranges[i][1])),
    done: false,
  }));
};

export const GET_SEEN_TEMPLATE = {
  pl: {
    name: "BĄDŹ WIDOCZNY",
    subtitle: "Pokaż światu rolę, którą chcesz zagrać",
    eyebrow: "BĄDŹ WIDOCZNY",
    tileBody: "Wybierz rolę, którą naprawdę chcesz zagrać, przygotuj scenę z LEO, nagraj self-tape i pokaż swoją pracę branży. 10 etapów, zalecane tempo: 30 dni.",
    paceLabel: "Ścieżka kariery dla aktorów · 10 etapów",
    yourPaceLabel: "Twoje tempo",
    daysWord: "dni",
    recommendedNote: "zalecane: 30 dni",
    aboutLabel: "Opis",
    intro: `Nie musisz czekać na zaproszenie na casting, żeby pokazać, w jakiej roli chcesz być obsadzany.

W tej ścieżce wybierzesz postać, którą naprawdę chciałbyś zagrać, samodzielnie znajdziesz odpowiednią scenę, przygotujesz ją z pomocą LEO i nagrasz self-tape prezentujący Twoje możliwości aktorskie.

Następnie wykorzystasz nagranie, żeby świadomie zaprezentować swoją pracę wybranym osobom z branży filmowej i castingowej oraz szerszej publiczności.`,
    goal: "Cel: w ciągu 30 dni przejść od pomysłu na rolę do gotowego self-tape’a i konkretnych działań zwiększających Twoją widoczność zawodową.",
    goalFlexible: "Cel: w wybranym przez Ciebie czasie ({days} dni) przejść od pomysłu na rolę do gotowego self-tape’a i konkretnych działań zwiększających Twoją widoczność zawodową.",
    accomplishTitle: "Co osiągniesz?",
    accomplish: [
      "Określisz rolę, w której chcesz zostać zauważony.",
      "Przygotujesz i nagrasz scenę prezentującą Twój potencjał.",
      "Zidentyfikujesz osoby z branży, którym warto pokazać swoją pracę.",
      "Podejmiesz indywidualne działania kontaktowe.",
      "Stworzysz materiał, który może wspierać Twoją karierę również po zakończeniu programu.",
    ],
    stepLabel: "Etap",
    daysLabel: "Dni",
    dayLabel: "Dzień",
    dueLabel: "do",
    whyLabel: "Dlaczego to ważne?",
    taskLabel: "Twoje zadanie",
    exampleLabel: "Przykład",
    resultLabel: "Rezultat",
    answerPlaceholder: "Twoja odpowiedź…",
    savingLabel: "Zapisywanie…",
    savedLabel: "Zapisano",
    saveErrorLabel: "Błąd zapisu — spróbuj ponownie",
    steps: [
      {
        title: "Zdecyduj, jak chcesz być postrzegany",
        why: `Branża często poznaje aktora przez role, które już zagrał. Ale Twoja dotychczasowa filmografia nie musi wyznaczać granic Twojej przyszłej kariery.

Zastanów się, jakie postacie naprawdę chcesz grać. Nie tylko te, do których obecnie jesteś zapraszany, lecz również te, w których możesz pokazać inną stronę swojego talentu.`,
        task: `Otwórz swój Actor Profile w LEO.
Przeanalizuj doświadczenie, umiejętności, mocne strony i dotychczasowe role.
Odpowiedz na pytania:`,
        fields: [
          { key: "question1", label: "Jakie trzy typy postaci najbardziej chciałbym grać?" },
          { key: "question2", label: "W jakich rolach widzę swój największy potencjał?" },
          { key: "question3", label: "Jakiej strony mojego aktorstwa branża jeszcze nie zna?" },
          { key: "question4", label: "Z jakim rodzajem historii chciałbym być kojarzony?" },
        ],
        result: "Lista trzech typów ról, które chcesz rozwijać.",
        link: { to: "/career?view=actor_profile", label: "Otwórz Actor Profile" },
      },
      {
        title: "Wybierz jedną konkretną rolę",
        why: `Jeden wyrazisty materiał aktorski może powiedzieć o Tobie więcej niż kilka przypadkowych scen.

Nie próbuj pokazać wszystkiego naraz. Skoncentruj się na jednej postaci, która pozwoli Ci zaprezentować konkretną stronę swojego aktorstwa.`,
        task: `Wybierz jeden typ roli z poprzedniego etapu.
Opisz postać, którą chciałbyś zagrać:`,
        fields: [
          { key: "question1", label: "Kim jest?" },
          { key: "question2", label: "Czego pragnie?" },
          { key: "question3", label: "Co ukrywa?" },
          { key: "question4", label: "Z jakim konfliktem się mierzy?" },
          { key: "question5", label: "Dlaczego właśnie Ty chcesz ją zagrać?" },
        ],
        after: "Nie musisz wybierać istniejącej postaci z konkretnego filmu. Możesz określić typ bohatera, na przykład ambitnego prokuratora, nieprzewidywalnego antagonistę, młodego ojca w kryzysie albo bohaterkę thrillera psychologicznego.",
        result: "Jedna wybrana rola i jasny cel nagrania.",
      },
      {
        title: "Znajdź scenę, która pokaże Twój potencjał",
        why: `Dobra scena do self-tape’a nie musi być najbardziej dramatyczną sceną, jaką znajdziesz.

Powinna dawać Ci przestrzeń do działania, podejmowania decyzji, reagowania i pokazania wewnętrznego życia postaci.`,
        task: `Samodzielnie znajdź scenę, która odpowiada wybranej roli.

Zwróć uwagę, czy:
• postać ma wyraźny cel;
• między bohaterami istnieje konflikt lub napięcie;
• scena pozwala pokazać zmianę emocjonalną;
• tekst pasuje do Twoich możliwości i kierunku zawodowego;
• scena nadaje się do nagrania w prostych warunkach.

Wybierz materiał, do którego masz odpowiednie prawa lub zgodę na planowany sposób wykorzystania, szczególnie jeśli zamierzasz publikować nagranie publicznie.`,
        result: "Wybrana scena do przygotowania.",
      },
      {
        title: "Przygotuj scenę z LEO",
        why: `Self-tape nie powinien być tylko poprawnie wypowiedzianym tekstem.

Chcesz pokazać twórcom, że potrafisz zbudować postać, rozumiesz sytuację dramatyczną i podejmujesz interesujące decyzje aktorskie.`,
        task: `Przejdź do modułu pracy nad sceną w LEO.

Wykorzystaj dostępne narzędzia do:
• analizy sytuacji i relacji między postaciami;
• określenia celu bohatera;
• zrozumienia przeszkód i konfliktu;
• poszukiwania działań i interpretacji;
• pracy nad rytmem oraz zmianami w scenie;
• nauki i utrwalenia tekstu.

Nie szukaj jednej „poprawnej” interpretacji. Sprawdzaj różne możliwości i podejmuj własne decyzje aktorskie.`,
        result: "Przygotowana scena, którą potrafisz zagrać świadomie i swobodnie.",
        link: { to: "/dashboard", label: "Przejdź do pracy nad sceną" },
      },
      {
        title: "Nagraj self-tape",
        why: `Twoim celem nie jest stworzenie perfekcyjnej produkcji filmowej.

Chodzi o to, żeby odbiorca mógł zobaczyć Twoją grę, obecność przed kamerą i potencjał do zagrania wybranej roli.`,
        task: `Przygotuj proste warunki nagrania:
• neutralne tło;
• czytelny obraz;
• dobre oświetlenie twarzy;
• wyraźny dźwięk;
• odpowiednie kadrowanie.

Nagraj kilka wersji sceny, jeśli tego potrzebujesz.

Wybierz ujęcie, które najlepiej pokazuje prawdę postaci, Twoje decyzje aktorskie i jakość gry — niekoniecznie to, w którym wszystko jest technicznie idealne.`,
        result: "Gotowy self-tape w wybranej roli.",
      },
      {
        title: "Przygotuj materiał do pokazania branży",
        why: `Samo nagranie to dopiero początek. Ważne jest również, jak przedstawisz je odbiorcom.

Twórca powinien szybko zrozumieć, kim jesteś, jaki rodzaj roli prezentujesz i dlaczego przesyłasz mu ten materiał.`,
        task: "Przygotuj:",
        checklist: {
          key: "step6",
          items: [
            "Link do nagrania, który można łatwo otworzyć.",
            "Krótki opis sceny i typu roli.",
            "Aktualne materiały zawodowe.",
            "Kilkuzdaniowa wiadomość przedstawiająca Ciebie i cel kontaktu.",
          ],
        },
        example: "„Przygotowałem scenę pokazującą mnie w roli bohatera thrillera psychologicznego. To kierunek aktorski, który chciałbym rozwijać i w którym szczególnie zależy mi na nowych możliwościach zawodowych”.",
        result: "Profesjonalnie przygotowany materiał do udostępnienia.",
      },
      {
        title: "Wybierz osoby, którym chcesz się pokazać",
        why: `Nie chodzi o wysłanie self-tape’a do jak największej liczby przypadkowych osób.

Chodzi o dotarcie do twórców, którzy pracują nad historiami i postaciami odpowiadającymi Twojemu kierunkowi zawodowemu.`,
        task: "Zidentyfikuj odpowiednich twórców:",
        checklist: {
          key: "step7",
          items: [
            "Reżyserzy castingu.",
            "Reżyserzy filmowi i serialowi.",
            "Producenci.",
            "Inni twórcy związani z interesującymi Cię projektami.",
          ],
        },
        after: `Sprawdź ich dotychczasową pracę, aktualne projekty i preferowane sposoby kontaktu.
Dodaj wybrane osoby do Career CRM w LEO.

Cel roboczy: przygotuj listę 10 trafnie dobranych kontaktów.

Nie zakładaj, że każda osoba przyjmuje niezamówione self-tape’y. Szanuj zasady kontaktu i preferencje odbiorców.`,
        result: "Lista konkretnych osób, do których chcesz dotrzeć.",
        link: { to: "/crm", label: "Otwórz Career CRM" },
      },
      {
        title: "Pokaż swoją pracę branży",
        why: `Twoja praca aktorska nie musi istnieć wyłącznie w oczekiwaniu na zaproszenie na casting.

Możesz świadomie przedstawiać swój kierunek zawodowy, budować relacje i pokazywać twórcom, jakie postacie chcesz grać.`,
        task: `Wybierz z przygotowanej listy osoby, z którymi kontakt jest uzasadniony i odpowiedni.
Przygotuj dla każdej z nich krótką, indywidualną wiadomość.

Wyjaśnij:
• kim jesteś;
• dlaczego kontaktujesz się właśnie z tą osobą;
• jaki typ roli pokazuje Twoje nagranie;
• gdzie można obejrzeć materiał.

Jeśli odbiorca nie przyjmuje niezamówionych materiałów, wybierz inną właściwą drogę budowania relacji.

Zapisz podjęte działania w Career CRM.`,
        result: "Pierwsze świadome działania zwiększające Twoją widoczność w branży.",
        link: { to: "/crm", label: "Otwórz Career CRM" },
      },
      {
        title: "Pokaż się światu",
        why: `Twoje media społecznościowe mogą pokazywać nie tylko codzienność, ale również to, jakim jesteś aktorem i w jakim kierunku chcesz się rozwijać.

Nie publikujesz po to, żeby zbierać przypadkowe wyświetlenia.
Publikujesz po to, żeby pokazać konkretną stronę swojego aktorstwa.`,
        task: `Jeśli masz prawa do publicznego udostępnienia sceny, przygotuj publikację na Instagramie, Facebooku lub innej odpowiedniej platformie.
Możesz opublikować cały self-tape albo jego fragment.

Dodaj krótki opis:`,
        checklist: {
          key: "step9",
          items: [
            "Jaką rolę przedstawiasz.",
            "Dlaczego wybrałeś tę postać.",
            "Co chciałeś pokazać w tej scenie.",
          ],
        },
        example: "„Zawsze chciałem zmierzyć się z rolą bohatera, który pozornie ma wszystko pod kontrolą, ale wewnętrznie się rozpada. Przygotowałem tę scenę, żeby pokazać właśnie taką stronę swojego aktorstwa”.",
        after: "Możesz też dodać, dlaczego akurat taki rodzaj bohatera, konfliktu lub tematu Cię porusza.",
        result: "Publiczna prezentacja Twojej pracy aktorskiej, jeśli zdecydujesz się na publikację.",
      },
      {
        title: "Zamień jeden self-tape w długofalowe działanie",
        why: `Jeden self-tape nie gwarantuje castingu ani roli.

Może jednak być początkiem nowych relacji, kolejnych rozmów i bardziej świadomego budowania Twojego wizerunku zawodowego.

Największa wartość tego programu nie polega na jednorazowej publikacji, ale na regularności.`,
        task: `Podsumuj ten cykl w sekcji Mastermind.

Zastanów się, czego nauczyłeś się o sobie jako aktorze, jak przebiegała praca nad sceną i jakie działania podjąłeś, żeby pokazać swoją pracę branży.

Zaplanuj odpowiednie dalsze działania i follow-upy, uwzględniając preferencje odbiorców.

Na koniec wybierz kolejną rolę, w której chcesz się pokazać.`,
        result: "Ukończony cykl BĄDŹ WIDOCZNY i konkretny plan dalszych działań.",
        link: { to: "/career/mastermind-next", label: "Otwórz Mastermind" },
      },
    ],
    completion: {
      title: "Gratulacje! Ukończyłeś BĄDŹ WIDOCZNY",
      lead: "W tym cyklu:",
      items: [
        "Wybrałeś rolę, którą naprawdę chcesz zagrać.",
        "Samodzielnie znalazłeś odpowiednią scenę.",
        "Przygotowałeś ją z pomocą LEO.",
        "Nagrałeś materiał pokazujący Twoje możliwości.",
        "Zidentyfikowałeś osoby z branży, z którymi warto budować relacje.",
        "Podjąłeś konkretne działania, aby pokazać swoją pracę.",
        "Zaplanowałeś kolejny krok zawodowy.",
      ],
      closing: "Nie musisz czekać, aż ktoś odkryje Twój potencjał. Możesz konsekwentnie pokazywać, co potrafisz i w jakich rolach chcesz być obsadzany.",
      nextTitle: "Co dalej?",
      next: `Wybierz nową rolę. Znajdź kolejną scenę. Rozwijaj kontakty.

BĄDŹ WIDOCZNY to nie jednorazowe wyzwanie. To nawyk świadomego budowania kariery aktorskiej.`,
      repeatCta: "Zacznij nowy cykl z nową rolą",
    },
  },

  en: {
    name: "GET SEEN",
    subtitle: "Show the Industry the Roles You Want to Play",
    eyebrow: "LEO GET SEEN",
    tileBody: "Choose a role you genuinely want to play, prepare a scene with LEO, record a self-tape, and put your work in front of the industry. 10 steps, recommended pace: 30 days.",
    paceLabel: "A Career Path for Actors · 10 steps",
    yourPaceLabel: "Your pace",
    daysWord: "days",
    recommendedNote: "recommended: 30 days",
    aboutLabel: "About This Career Path",
    intro: `You don't have to wait for an audition to show the industry what you're capable of.

In this Career Path, you'll choose the kind of character you genuinely want to play, find a scene that brings that character to life, prepare it using LEO, and record a self-tape that showcases your abilities.

Then you'll put that work to use — introducing yourself to relevant casting directors, filmmakers, and producers, and sharing your performance with a wider audience.`,
    goal: "Your goal: Go from imagining your next great role to having a finished self-tape and a clear plan for getting your work in front of the right people — all within 30 days.",
    goalFlexible: "Your goal: Go from imagining your next great role to having a finished self-tape and a clear plan for getting your work in front of the right people — all within the {days} days you've chosen.",
    accomplishTitle: "What You'll Accomplish",
    accomplish: [
      "Identify the kinds of roles you want to be considered for.",
      "Prepare and record a scene that showcases your abilities.",
      "Identify industry professionals whose work aligns with your goals.",
      "Take intentional steps to introduce yourself and build professional relationships.",
      "Create a piece of work you can continue using beyond this challenge.",
    ],
    stepLabel: "Step",
    daysLabel: "Days",
    dayLabel: "Day",
    dueLabel: "by",
    whyLabel: "Why This Matters",
    taskLabel: "Your Assignment",
    exampleLabel: "Example",
    resultLabel: "Your outcome",
    answerPlaceholder: "Your answer…",
    savingLabel: "Saving…",
    savedLabel: "Saved",
    saveErrorLabel: "Couldn't save — please try again",
    steps: [
      {
        title: "Decide How You Want the Industry to See You",
        why: `The industry often sees actors through the lens of the roles they've already played. But your past credits don't have to define your future.

Think about the characters you're genuinely excited to explore — not just the roles you're currently getting auditions for, but the ones that would allow you to reveal another side of yourself as an actor.`,
        task: `Open your Actor Profile in LEO.
Review your experience, skills, strengths, and previous roles.
Answer the following questions:`,
        fields: [
          { key: "question1", label: "What three types of characters would you most love to play?" },
          { key: "question2", label: "Which roles would allow you to showcase your greatest strengths as an actor?" },
          { key: "question3", label: "What side of your acting hasn't the industry seen yet?" },
          { key: "question4", label: "What kinds of stories do you want to be part of?" },
        ],
        result: "Three types of roles you want to pursue.",
        link: { to: "/career?view=actor_profile", label: "Open Actor Profile" },
      },
      {
        title: "Choose One Role to Focus On",
        why: `You don't need to show everything you can do in one performance.

One compelling, well-chosen scene can make a stronger impression than several unrelated clips.`,
        task: `Choose one type of role from Step 1.
Now imagine the character you want to bring to life:`,
        fields: [
          { key: "question1", label: "Who is this character?" },
          { key: "question2", label: "What do they want more than anything?" },
          { key: "question3", label: "What are they hiding?" },
          { key: "question4", label: "What conflict are they facing?" },
          { key: "question5", label: "Why do you personally want to play this character?" },
        ],
        after: `You don't have to choose an existing character from a particular film or television series.

You might envision an ambitious prosecutor, an unpredictable antagonist, a young father struggling to hold his family together, or a complicated lead in a psychological thriller.`,
        result: "One clearly defined role and a specific creative goal for your self-tape.",
      },
      {
        title: "Find a Scene That Shows What You Can Do",
        why: `The best self-tape scene isn't necessarily the one with the biggest emotional breakdown or the most dramatic dialogue.

It's the one that gives you something compelling to play.

Look for a scene where your character has a clear objective, faces resistance, makes choices, and responds to another person.`,
        task: `Find a scene on your own that fits the type of character you've chosen.

As you explore your options, ask yourself:
• Does the character have a clear objective?
• Is there meaningful conflict or tension?
• Does the scene give me room to make interesting acting choices?
• Can I show a shift in the character's emotional or psychological state?
• Does the material support the direction I want to take my career?
• Can I shoot it effectively with a simple self-tape setup?

Make sure you have the necessary rights or permission for how you plan to use the scene, especially if you intend to share it publicly.`,
        result: "A scene you've chosen and are ready to work on.",
      },
      {
        title: "Prepare Your Scene With LEO",
        why: `A strong self-tape is more than memorized lines delivered convincingly.

It's an opportunity to show casting directors and filmmakers how you build a character, understand dramatic circumstances, and make specific, truthful choices.`,
        task: `Open LEO's Scene Work tools.

Use the available features to explore:
• The circumstances of the scene and the relationships between the characters.
• Your character's objective.
• The obstacles and conflicts standing in their way.
• Possible actions, tactics, and interpretations.
• The scene's rhythm, turning points, and emotional shifts.
• Learning and internalizing your lines.

Don't look for a single "correct" interpretation.
Explore different possibilities, make your own choices, and find the version of the scene that feels most alive to you.`,
        result: "A fully prepared scene you can perform with confidence, specificity, and freedom.",
        link: { to: "/dashboard", label: "Open Scene Work" },
      },
      {
        title: "Record Your Self-Tape",
        why: `You're not trying to produce a short film.

You're creating an acting sample that lets someone see your presence on camera, your choices, and your potential to play this kind of role.

The performance matters more than production value.`,
        task: `Set up a simple, professional recording space.

Make sure you have:
• A clean, neutral background.
• A clear image.
• Good lighting on your face.
• Clean, understandable audio.
• Framing that keeps the focus on your performance.

Record several takes if you need to.

Review your work and choose the take that best captures the character's truth, your acting choices, and your connection to the scene.

Don't automatically choose the most technically perfect take. Choose the one that feels most compelling and alive.`,
        result: "A finished self-tape showcasing you in the kind of role you want to play.",
      },
      {
        title: "Get Your Materials Ready to Share",
        why: `Recording a strong scene is only the beginning.
How you present your work matters, too.

Someone receiving your self-tape should quickly understand who you are, what kind of role you're showcasing, and why you're sharing it.`,
        task: "Get these ready:",
        checklist: {
          key: "step6",
          items: [
            "Create an easy-to-access link to your self-tape.",
            "Write a short description of the scene and the type of role it showcases.",
            "Make sure your headshots, résumé, and other relevant acting materials are up to date.",
            "Draft a short, professional introduction explaining who you are and why you're reaching out.",
          ],
        },
        example: "“I recently put together a scene exploring a psychologically complex character in a thriller. It's the kind of material I'm excited to pursue, and I wanted to share a sample of my work that reflects that direction.”",
        after: "Keep your introduction personal, confident, and concise.",
        result: "A professional, ready-to-share package that presents both your work and your career direction.",
      },
      {
        title: "Identify the People You Want to Reach",
        why: `Getting seen isn't about sending your self-tape to as many people as possible.

It's about connecting with professionals whose work genuinely aligns with the kinds of stories and characters you want to be part of.

A thoughtful, targeted approach is more valuable than a generic mass email.`,
        task: "Research industry professionals who work on projects relevant to your goals:",
        checklist: {
          key: "step7",
          items: [
            "Casting directors.",
            "Film and television directors.",
            "Producers.",
            "Other filmmakers and creative professionals working on projects that interest you.",
          ],
        },
        after: `Look at their previous work, current projects, and preferred methods of professional contact.
Add relevant people to your Career CRM in LEO.

Suggested target: Build a focused list of 10 industry contacts.

Before reaching out, check whether they accept unsolicited materials. Some casting offices and representatives have specific submission policies, and those boundaries should always be respected.`,
        result: "A targeted list of industry professionals you'd like to connect with.",
        link: { to: "/crm", label: "Open Career CRM" },
      },
      {
        title: "Put Your Work in Front of the Industry",
        why: `Your work as an actor doesn't have to exist only when you're invited to audition.

You can take an active role in introducing yourself, communicating your artistic direction, and building meaningful professional relationships.

The goal isn't to demand an opportunity.
It's to make a thoughtful introduction and give the right people a chance to discover your work.`,
        task: `Review the contacts you identified in Step 7.
Choose people you have a legitimate reason to contact and who are open to receiving professional introductions or acting materials.
Write a brief, personalized message for each appropriate contact.

Include:
• Who you are.
• Why you're reaching out to this particular person.
• What kind of role your self-tape showcases.
• Where they can view your work, if sharing it is appropriate.

If someone doesn't accept unsolicited submissions, respect that policy and look for another appropriate way to develop the professional relationship.

Track your outreach and relevant follow-up plans in LEO's Career CRM.`,
        result: "Concrete steps toward increasing your visibility and building industry relationships.",
        link: { to: "/crm", label: "Open Career CRM" },
      },
      {
        title: "Share Your Work With the World",
        why: `Your social media presence can be more than behind-the-scenes photos, personal updates, and announcements about past projects.

It can also show people the actor you're becoming.

You're not posting just to collect views or likes.
You're sharing a piece of work that communicates your creative interests and the kinds of characters you want to explore.`,
        task: `If you have the rights to share your scene publicly, prepare a post for Instagram, Facebook, or another platform that makes sense for your audience.
You can share the full self-tape or a carefully selected excerpt.

In your caption:`,
        checklist: {
          key: "step9",
          items: [
            "Explain what kind of character you're portraying.",
            "Explain why you chose this particular character.",
            "Explain what you wanted to explore or reveal through the performance.",
          ],
        },
        example: `“I've always been drawn to characters who seem completely in control on the outside while everything is falling apart underneath.
I wanted to explore that contradiction in this scene — the tension between what someone shows the world and what they're actually experiencing.
These are the kinds of layered, emotionally complex roles I love bringing to life.”`,
        after: "You can also share why this type of character, conflict, or subject matter resonates with you personally.",
        result: "A public showcase of your acting work, if you choose to share it.",
      },
      {
        title: "Turn One Self-Tape Into Long-Term Career Momentum",
        why: `One self-tape won't guarantee an audition, a callback, or a booking.

But it can become the starting point for new relationships, future conversations, and a more intentional approach to how you position yourself as an actor.

The real value of GET SEEN isn't in making one video.
It's in building a repeatable habit of creating work and putting yourself out there.`,
        task: `Open the Mastermind section in LEO and reflect on this cycle.

Consider what you learned about yourself as an actor, the role you chose, the scene you prepared, and the actions you took to get your work seen.

Review your Career CRM and plan any appropriate next steps or follow-ups, always respecting each contact's preferences.

Finally, choose the next type of role you'd like to explore.`,
        result: "A completed GET SEEN challenge and a clear plan for your next round of career-building actions.",
        link: { to: "/career/mastermind-next", label: "Open Mastermind" },
      },
    ],
    completion: {
      title: "Congratulations! You've Completed LEO GET SEEN",
      lead: "Over this cycle, you've:",
      items: [
        "Chosen a role you genuinely want to play.",
        "Found a scene that reflects your creative goals.",
        "Prepared that scene using LEO.",
        "Recorded a self-tape that showcases your abilities.",
        "Identified industry professionals you'd like to connect with.",
        "Taken intentional steps to put your work in front of others.",
        "Created a plan to keep building momentum.",
      ],
      closing: "You don't have to wait for someone to discover your potential. You can take ownership of your career and consistently show the industry what you're capable of — and the roles you're ready to play.",
      nextTitle: "What's Next?",
      next: `Choose another role.
Find another scene.
Keep creating.
Keep building relationships.

GET SEEN isn't a one-time challenge. It's a new way to take charge of your acting career.`,
      repeatCta: "Start a new cycle with a new role",
    },
  },
};
